import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, X, SlidersHorizontal } from "lucide-react";

const CONDITIONS = ["New", "Like New", "Excellent", "Very Good", "Good", "Fair", "For Parts / Repair"];
const SORTS = [
  { v: "newest", l: "Newest" },
  { v: "oldest", l: "Oldest" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
  { v: "name", l: "Name" },
];

export default function Inventory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const filters = useMemo(() => ({
    category: searchParams.get("category") || "",
    brand: searchParams.get("brand") || "",
    condition: searchParams.get("condition") || "",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
    search: searchParams.get("search") || "",
    sort: searchParams.get("sort") || "newest",
  }), [searchParams]);

  const set = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "" || v == null) next.delete(k); else next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => setSearchParams({}, { replace: true });

  useEffect(() => {
    (async () => {
      try {
        const [c, b] = await Promise.all([api.get("/categories"), api.get("/brands")]);
        setCategories(c.data);
        setBrands(b.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
        const { data } = await api.get("/products", { params });
        if (!cancelled) setProducts(data.items);
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [filters]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Catalog</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Inventory</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">Browse every laptop, desktop, monitor, and accessory currently on the shelf. Filter, sort, and open a product for full specs.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="inventory-search-input"
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
            placeholder="Search by name, model, brand, SKU…"
            className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          data-testid="inventory-sort-select"
          value={filters.sort}
          onChange={(e) => set("sort", e.target.value)}
          className="px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {SORTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <button
          onClick={() => setShowFilters(v => !v)}
          data-testid="inventory-toggle-filters"
          className="md:hidden inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium"
        >
          <SlidersHorizontal className="w-4 h-4" /> Filters
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
        <aside className={`space-y-6 ${showFilters ? "" : "hidden md:block"}`} data-testid="inventory-filters">
          <FilterBlock title="Category">
            <Options
              testid="filter-category"
              options={categories.map(c => c.name)}
              value={filters.category}
              onChange={(v) => set("category", v === filters.category ? "" : v)}
            />
          </FilterBlock>
          <FilterBlock title="Brand">
            <Options
              testid="filter-brand"
              options={brands}
              value={filters.brand}
              onChange={(v) => set("brand", v === filters.brand ? "" : v)}
            />
          </FilterBlock>
          <FilterBlock title="Condition">
            <Options
              testid="filter-condition"
              options={CONDITIONS}
              value={filters.condition}
              onChange={(v) => set("condition", v === filters.condition ? "" : v)}
            />
          </FilterBlock>
          <FilterBlock title="Price">
            <div className="flex gap-2">
              <input
                data-testid="filter-min-price"
                type="number"
                placeholder="Min"
                value={filters.min_price}
                onChange={(e) => set("min_price", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
              />
              <input
                data-testid="filter-max-price"
                type="number"
                placeholder="Max"
                value={filters.max_price}
                onChange={(e) => set("max_price", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
              />
            </div>
          </FilterBlock>
          <button
            data-testid="filter-clear-all"
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            <X className="w-3.5 h-3.5" /> Clear filters
          </button>
        </aside>

        <div>
          <div className="text-xs font-medium text-slate-500 mb-4">
            {loading ? "Loading…" : `${products.length} product${products.length === 1 ? "" : "s"}`}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] rounded-2xl bg-white border border-slate-200 animate-pulse" />
              ))}
            </div>
          ) : products.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="inventory-grid">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
              No products match your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterBlock({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}
function Options({ options, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      {options.map(o => (
        <button
          key={o}
          data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onChange(o)}
          className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
            value === o ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
